import React, { useMemo, useState } from "react";
import {
    Headline,
    Loading,
    useMerchantConfig,
    useThemedStyle,
} from "@venuepos/react-common";
import { format } from "date-fns";
import { AvailableLocale, useTranslation } from "locales";
import {
    VictoryAxis,
    VictoryBar,
    VictoryChart,
    VictoryTheme,
    VictoryGroup,
    VictoryTooltip,
} from "victory";
import { dashboardStyleFunc } from "../styles";
import { formatAmount } from "lib";
import { CompareData } from "../merchant-dashboard";
import { VictoryLegend } from "victory-native";
import { View } from "react-native";

const colorScale = ["#201c35", "#909090"]; // = [theme.colors.primary, theme.colors.grey500]

// Build list of ticks for 24 hours starting at 06:00.
const ticks = (() => {
    const ticksArray = [];
    const offset = 6; // Start at 06:00
    for (let i = 0 + offset; i < 24 + offset; i++) {
        ticksArray.push(`${i % 24}`.padStart(2, "0"));
    }
    return ticksArray;
})();

export function InvoiceAmountPerHour({
    data,
    compareTo,
    locale,
}: {
    data?: CompareData;
    compareTo?: CompareData;
    locale?: AvailableLocale;
}) {
    const merchantConfig = useMerchantConfig();
    const [t] = useTranslation();
    const sharedStyles = useThemedStyle(dashboardStyleFunc);

    const [boundingRect, setBoundingRect] = useState({
        width: 0,
        height: 0,
    });

    const amountData = useMemo(() => {
        const returnVal = [];

        if (data?.dashboard?.amountsByHour) {
            const baseDataSet = {
                day: data.date,
                data: ticks
                    .map(tickItr => {
                        const gotData = data.dashboard?.amountsByHour.find(
                            itr =>
                                format(new Date(itr.datetime), "HH") === tickItr
                        );

                        if (!gotData) {
                            return undefined;
                        }

                        return {
                            hour: tickItr.toString(),
                            sum: gotData?.sum || 0,
                        };
                    })
                    .filter(Boolean),
            };

            returnVal.push(baseDataSet);
        }

        if (!compareTo) {
            return returnVal;
        }

        if (compareTo?.dashboard?.amountsByHour) {
            const compareDataSet = {
                day: compareTo?.date,
                data: ticks
                    .map(tickItr => {
                        const gotData = compareTo.dashboard.amountsByHour.find(
                            itr =>
                                format(new Date(itr.datetime), "HH") === tickItr
                        );

                        if (!gotData) {
                            return null;
                        }

                        return {
                            hour: tickItr.toString(),
                            sum: gotData?.sum || 0,
                        };
                    })
                    .filter(Boolean),
            };

            returnVal.push(compareDataSet);
        }

        return returnVal;
    }, [compareTo, data.dashboard?.amountsByHour, data.date]);

    const padding = { left: 90, bottom: 50, top: 60, right: 10 };
    return (
        <>
            <Headline style={sharedStyles.totalHeadline} size="h5">
                {t(
                    "dashboard.widgets.invoice_amount_per_hour",
                    "Invoice amount per hour"
                )}
            </Headline>
            {data ? (
                <View
                    onLayout={e =>
                        setBoundingRect({
                            width: e.nativeEvent.layout.width,
                            height: e.nativeEvent.layout.height,
                        })
                    }
                >
                    <VictoryChart
                        height={300}
                        width={boundingRect.width}
                        padding={padding}
                        theme={VictoryTheme.material}
                        domainPadding={{
                            x: Math.max(Math.ceil(boundingRect.width / 24), 8),
                        }}
                    >
                        <VictoryLegend
                            title={t("common.date", "Date")}
                            colorScale={colorScale}
                            centerTitle
                            orientation="horizontal"
                            gutter={15}
                            style={{
                                border: {
                                    stroke: "lightgray",
                                    strokeWidth: 1,
                                },
                                title: {
                                    fontWeight: "bold",
                                },
                            }}
                            data={amountData?.map(itr => ({ name: itr.day }))}
                        />

                        <VictoryAxis
                            dependentAxis
                            tickFormat={tick => `${formatAmount(tick)}`}
                        />
                        <VictoryAxis crossAxis tickValues={ticks} />

                        <VictoryGroup offset={10} colorScale={colorScale}>
                            {amountData?.map(dataItr => (
                                <VictoryBar
                                    barWidth={Math.min(
                                        Math.ceil(boundingRect.width / 24),
                                        8
                                    )}
                                    x="hour"
                                    y={d => d.sum}
                                    data={dataItr.data}
                                    cornerRadius={2}
                                    labels={({ datum }) =>
                                        `${dataItr.day}, ${
                                            datum.hour
                                        }:\n${formatAmount(
                                            datum.sum,
                                            merchantConfig.currency,
                                            {
                                                locale: locale as AvailableLocale,
                                                printCurrency: true,
                                            }
                                        )}`
                                    }
                                    labelComponent={<VictoryTooltip />}
                                />
                            ))}
                        </VictoryGroup>
                    </VictoryChart>
                </View>
            ) : (
                <View style={sharedStyles.widgetContainer}>
                    <Loading message={t("common.loading", "Loading")} />
                </View>
            )}
        </>
    );
}
